import React, { useContext, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CartContext } from "./CartContext";
import productData, {
  formatPrice,
  getShippingRate,
  siteConfig,
} from "../Datas/productData";
import "../css/cartpage.scss";

const CartPage = () => {
  const navigate = useNavigate();
  const { cartItems, removeFromCart, updateQuantity, clearCart } =
    useContext(CartContext);

  const cartLines = useMemo(
    () =>
      cartItems
        .map((item) => {
          const product = productData.find(
            (candidate) => String(candidate.id) === String(item.id)
          );

          if (!product) return null;

          const quantity = Math.max(1, Number(item.quantity) || 1);
          return {
            ...item,
            product,
            quantity,
            lineTotal: Number(product.price) * quantity,
          };
        })
        .filter(Boolean),
    [cartItems]
  );

  const subtotal = useMemo(
    () => cartLines.reduce((total, item) => total + item.lineTotal, 0),
    [cartLines]
  );

  const shipping = getShippingRate();
  const total = subtotal + shipping;
  const shippingProgress = 100;

  const updateLineQuantity = (item, nextQuantity) => {
    if (nextQuantity < 1) return;
    updateQuantity(item.id, item.size, item.color, nextQuantity);
  };

  const goToCheckout = () => {
    navigate("/checkout");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (cartLines.length === 0) {
    return (
      <main className="cart-page cart-page--empty" aria-labelledby="empty-cart-title">
        <section className="cart-empty">
          <span className="cart-empty__icon" aria-hidden="true">
            <svg viewBox="0 0 48 48" role="presentation">
              <path d="M13 16h22l-2 24H15l-2-24Z" />
              <path d="M18 19v-6a6 6 0 0 1 12 0v6" />
            </svg>
          </span>
          <p className="cart-eyebrow">Your bag</p>
          <h1 id="empty-cart-title">A little room for something lovely.</h1>
          <p>
            Your shopping bag is currently empty. Explore thoughtful everyday
            pieces from {siteConfig.brandName}.
          </p>
          <Link className="cart-primary-button" to="/shop">
            Shop the collection
          </Link>
        </section>
      </main>
    );
  }

  return (
    <main className="cart-page" aria-labelledby="cart-title">
      <div className="cart-page__heading">
        <p className="cart-eyebrow">Your selection</p>
        <h1 id="cart-title">Shopping bag</h1>
        <p>
          {(() => {
            const itemCount = cartLines.reduce(
              (count, item) => count + item.quantity,
              0
            );
            return `${itemCount} ${itemCount === 1 ? "item" : "items"}`;
          })()} reserved for checkout
        </p>
      </div>

      <div className="cart-page__layout">
        <section className="cart-page__items" aria-label="Items in your bag">
          <div className="cart-page__items-header" aria-hidden="true">
            <span>Product</span>
            <span>Quantity</span>
            <span>Total</span>
          </div>

          <div className="cart-items">
            {cartLines.map((item) => {
              const { product } = item;
              const lineKey = `${item.id}-${item.size || "default"}-${
                item.color || "default"
              }`;
              const image = product.images?.[0] || product.image;
              const variant = [item.color, item.size]
                .filter(Boolean)
                .join(" / ");

              return (
                <article
                  className="cart-item"
                  key={lineKey}
                >
                  <Link
                    className="cart-item__image-link"
                    to={`/shopDetails/${product.id}`}
                    aria-label={`View ${product.name}`}
                  >
                    <img
                      src={image}
                      alt={product.name}
                      width="1120"
                      height="1400"
                    />
                  </Link>

                  <div className="cart-item__details">
                    <p className="cart-item__category">{product.category}</p>
                    <h2>
                      <Link to={`/shopDetails/${product.id}`}>
                        {product.name}
                      </Link>
                    </h2>
                    {variant && <p className="cart-item__variant">{variant}</p>}
                    <p className="cart-item__unit-price">
                      {formatPrice(product.price)} each
                    </p>
                    <button
                      className="cart-item__remove"
                      type="button"
                      onClick={() =>
                        removeFromCart(item.id, item.size, item.color)
                      }
                      aria-label={`Remove ${product.name} from bag`}
                    >
                      Remove
                    </button>
                  </div>

                  <div className="cart-item__quantity">
                    <span className="visually-hidden" id={`quantity-${lineKey}`}>
                      Quantity for {product.name}
                    </span>
                    <div
                      className="cart-quantity"
                      role="group"
                      aria-labelledby={`quantity-${lineKey}`}
                    >
                      <button
                        type="button"
                        onClick={() =>
                          updateLineQuantity(item, item.quantity - 1)
                        }
                        disabled={item.quantity <= 1}
                        aria-label={`Decrease ${product.name} quantity`}
                      >
                        −
                      </button>
                      <output aria-live="polite">{item.quantity}</output>
                      <button
                        type="button"
                        onClick={() =>
                          updateLineQuantity(item, item.quantity + 1)
                        }
                        aria-label={`Increase ${product.name} quantity`}
                      >
                        +
                      </button>
                    </div>
                  </div>

                  <p className="cart-item__line-total">
                    {formatPrice(item.lineTotal)}
                  </p>
                </article>
              );
            })}
          </div>

          <div className="cart-page__item-actions">
            <Link className="cart-text-link" to="/shop">
              ← Continue shopping
            </Link>
            <button className="cart-text-link" type="button" onClick={clearCart}>
              Clear bag
            </button>
          </div>
        </section>

        <aside className="cart-summary" aria-labelledby="cart-summary-title">
          <div className="cart-shipping-progress">
            <p>
              Tracked shipping is {formatPrice(shipping)} per order.
            </p>
            <div
              className="cart-shipping-progress__track"
              role="progressbar"
              aria-label="Progress toward complimentary shipping"
              aria-valuemin="0"
              aria-valuemax="100"
              aria-valuenow={Math.round(shippingProgress)}
            >
              <span style={{ width: `${shippingProgress}%` }} />
            </div>
            <span>
              Flat-rate tracked delivery
            </span>
          </div>

          <h2 id="cart-summary-title">Order summary</h2>
          <dl className="cart-summary__totals">
            <div>
              <dt>Subtotal</dt>
              <dd>{formatPrice(subtotal)}</dd>
            </div>
            <div>
              <dt>Standard shipping</dt>
              <dd>{shipping === 0 ? "Complimentary" : formatPrice(shipping)}</dd>
            </div>
            <div className="cart-summary__grand-total">
              <dt>Total</dt>
              <dd>{formatPrice(total)}</dd>
            </div>
          </dl>
          <p className="cart-summary__note">
            Taxes, if applicable, are calculated at checkout. Shipping is
            estimated for the standard service.
          </p>
          <button
            className="cart-primary-button cart-primary-button--full"
            type="button"
            onClick={goToCheckout}
          >
            Continue to checkout
          </button>
          <p className="cart-summary__secure">Secure checkout · Card details entered on payment page</p>
        </aside>
      </div>
    </main>
  );
};

export default CartPage;
